import React, { useState, useEffect } from "react";
import { resolveLogin } from "../services/configService";

/**
 * Login.jsx
 * ---------
 * Self-contained login page.
 * Credentials are resolved via configService.resolveLogin which now calls
 * POST /api/auth/login on the FastAPI / PostgreSQL backend.
 *
 * Auth hierarchy (unchanged from static-JSON version):
 *   admin  → env REACT_APP_ADMIN_USERNAME / REACT_APP_ADMIN_PASSWORD
 *   fleet  → fleets.user_access / user_password  in DB
 *   vessel → vessels.user_access / user_password in DB
 *
 * Props:
 *   onLogin(role)  – called with "admin", "fleet", or "vessel"
 *
 * Performance fix for large login-bg.webp (12 MB):
 *   - Background colour (#0d5945) is shown instantly — no blank screen.
 *   - The image is loaded off-screen via a hidden <img> so the browser
 *     can stream/decode it while the user interacts with the form.
 *   - Once decoded it fades in smoothly (CSS transition).
 *   - A <link rel="preload"> tag is injected into <head> on mount so
 *     the browser fetches the asset at the highest network priority,
 *     even before any other render work.  In production (Azure Static
 *     Web Apps / App Service) you can also add the equivalent HTTP
 *     header in staticwebapp.config.json / web.config for a further
 *     boost — see comment at the bottom of this file.
 */
const Login = ({ onLogin }) => {
  const [bgReady, setBgReady]           = useState(false);
  const [username, setUsername]         = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState("");
  const [isLoading, setIsLoading]       = useState(false);

  // ── Aggressive preload: inject <link rel="preload"> once on mount ──
  useEffect(() => {
    if (!document.querySelector('link[data-login-bg]')) {
      const link = document.createElement("link");
      link.rel          = "preload";
      link.as           = "image";
      link.href         = "/assets/login-bg.webp";
      link.type         = "image/webp";
      link.dataset.loginBg = "1";
      document.head.prepend(link); // prepend = highest priority
    }

    // Also start decoding the image off-thread so it's ready to paint
    const img = new Image();
    img.src = "/assets/login-bg.webp";
    // decode() resolves after the browser has fully decoded the bitmap
    img.decode()
      .then(() => setBgReady(true))
      .catch(() => {
        // decode() not supported or image failed — fall back to onload/onerror
        img.onload  = () => setBgReady(true);
        img.onerror = () => setBgReady(true); // still show solid bg
      });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!username.trim() || !password) {
      setError("Please enter your username and password.");
      return;
    }

    setIsLoading(true);
    await new Promise(r => setTimeout(r, 400)); // brief visual delay

    try {
      const session = await resolveLogin(username.trim(), password);

      if (!session) {
        setError("Invalid username or password.");
        setIsLoading(false);
        return;
      }

      // Persist session so Main.jsx can scope data to the right vessel/fleet
      sessionStorage.setItem("auth_role", session.role);

      if (session.role === "fleet") {
        if (session.fleetKey)  sessionStorage.setItem("auth_fleet_key",  session.fleetKey);
        if (session.fleetInfo) sessionStorage.setItem("auth_fleet_info", JSON.stringify(session.fleetInfo));
        onLogin("fleet");
      } else if (session.role === "vessel") {
        if (session.vesselKey)  sessionStorage.setItem("auth_vessel_key",  session.vesselKey);
        if (session.vesselInfo) sessionStorage.setItem("auth_vessel_info", JSON.stringify(session.vesselInfo));
        onLogin("vessel");
      } else {
        onLogin("admin");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed — please check the server is running and try again.");
      setIsLoading(false);
    }
  };

  return (
    <div
      className="h-screen w-screen overflow-y-auto overflow-x-hidden relative bg-[#0d5945]"
      style={{ fontFamily: "'StevieSans', system-ui, sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.zeroquhq.com/steviewsans/stylesheet.css');
        *, *::before, *::after { font-family: 'StevieSans', system-ui, sans-serif; }
        * { color: #000000; }

        /* Background image layer — fades in once decoded */
        .login-bg-layer {
          position: fixed;
          inset: 0;
          background-image: url("/assets/login-bg.webp");
          background-size: cover;
          background-position: center;
          opacity: 0;
          transition: opacity 0.6s ease;
          pointer-events: none;
          z-index: 0;
        }
        .login-bg-layer.ready {
          opacity: 1;
        }
      `}</style>

      {/* Background image layer — invisible until decoded, then fades in */}
      <div className={`login-bg-layer${bgReady ? " ready" : ""}`} aria-hidden="true" />

      {/* Full-screen loading overlay */}
      {isLoading && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(255,255,255,0.80)', backdropFilter:'blur(4px)' }}>
          <style>{`
            @keyframes zq-spin-fade {
              0%   { opacity:0.15; transform:rotate(var(--a)) translateX(28px) scale(0.75); }
              50%  { opacity:1;    transform:rotate(var(--a)) translateX(28px) scale(1);    }
              100% { opacity:0.15; transform:rotate(var(--a)) translateX(28px) scale(0.75); }
            }
            .zq-dot { position:absolute; top:50%; left:50%; width:14px; height:14px; margin:-7px 0 0 -7px; border-radius:50%; background:#0d5a47; animation:zq-spin-fade 1.6s ease-in-out infinite; }
          `}</style>
          <div style={{ position:'relative', width:80, height:80 }}>
            {[0,45,90,135,180,225,270,315].map((deg, i) => (
              <span key={i} className="zq-dot" style={{ '--a':`${deg}deg`, animationDelay:`${i*200-1600}ms` }} />
            ))}
          </div>
        </div>
      )}

      {/* Login panel — full-width on mobile, right 53% on desktop */}
      <div className="
        relative z-10 w-full min-h-screen
        flex flex-col items-center
        px-6 py-12 text-center text-black
        bg-white/20 backdrop-blur-[10px]
        border-0 shadow-none
        xl:absolute xl:inset-auto xl:top-0 xl:right-0 xl:left-auto
        xl:w-[53vw] xl:min-h-screen xl:px-[30px] xl:py-[40px]
        xl:flex xl:flex-col xl:justify-start
      ">
        <div className="flex-1 flex flex-col justify-center w-full max-w-md mx-auto">

          {/* Logo */}
          <img
            src="/assets/Zeroqu-Logo.png"
            alt="Zeroqu Logo"
            loading="eager"
            className="h-[1.5em] w-auto max-w-full object-contain mb-[5px] mx-auto md:mx-0"
          />

          <h5 className="mt-2.5 text-black text-[18px] w-full md:w-[60%] mx-auto text-center">
            Engine Performance Analyser
          </h5>

          <h1 className="mt-5 text-black text-[28px] leading-tight w-full text-center tracking-wide">
            Log in to your account
          </h1>

          {/* Error banner */}
          {error && (
            <div className="
              bg-[rgba(230,166,150,0.37)] text-black
              border-l-4 border-[#994848] rounded-[5px]
              px-3 py-[10px] mb-4 text-left text-[14px]
              w-full md:w-1/2 mx-auto
            ">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleLogin} className="w-full max-w-[320px] mx-auto mt-4">
            <div className="w-full max-w-md mx-auto">

              {/* Username */}
              <div className="flex flex-col">
                <label htmlFor="username" className="block mb-[5px] text-left font-light text-black mt-1">
                  Username<span className="ml-[2px] text-black">*</span>
                </label>
                <input
                  id="username" type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={e => { setUsername(e.target.value); setError(""); }}
                  required autoComplete="username"
                  className="w-full p-[10px] rounded-[5px] border border-[#a4c681] text-[14px] text-black focus:outline-none"
                />
              </div>

              {/* Password */}
              <div className="flex flex-col mt-3">
                <label htmlFor="password" className="block mb-[5px] text-left font-light text-black">
                  Password<span className="ml-[2px] text-black">*</span>
                </label>
                <div className="relative">
                  <input
                    id="password" type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError(""); }}
                    required autoComplete="current-password"
                    className="w-full p-[10px] rounded-[5px] border border-[#a4c681] text-[14px] text-black focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-black"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                        <path d="m22 2-20 20" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit" disabled={isLoading}
                className="w-full mt-5 p-[10px] bg-[#0d5945] text-white font-bold rounded-[5px] cursor-pointer disabled:opacity-60"
              >
                {isLoading ? "Signing in…" : "Sign in"}
              </button>
            </div>
          </form>

          <div className="h-8 xl:h-12" />
        </div>
      </div>
    </div>
  );
};

export default Login;
